'use client';

import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { FacebookShareButton, LinkedinShareButton } from 'react-share';

import { Event, trackEvent } from '~/ui/components/analytics';
import { COPY_CLIPBOARD_TOGGLE_TIMEOUT } from '~/v1/constants/timings';

import styles from './share.module.scss';
import { Icon } from '../icons/icon';
import { IconType } from '../icons/icon.interfaces';

interface ShareProps {
  label: string;
  className?: string;
}

export const Share: React.FC<ShareProps> = ({ label, className }) => {
  const [hasRendered, setHasRendered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const clipboardTimeout: { current: NodeJS.Timeout | null } = useRef(null);

  const url = hasRendered ? window.location.href : '';
  useEffect(() => {
    setHasRendered(true);
  }, []);

  const toggleTooltip = () => {
    clipboardTimeout.current && clearTimeout(clipboardTimeout.current);
    setCopied(false);
    setIsVisible(!isVisible);
  };

  const copyToClipboard = () => {
    setCopied(true);

    trackEvent(Event.SOCIAL_MEDIA_SHARE, {
      socialType: 'Copied URL',
    });

    clipboardTimeout.current = setTimeout(() => {
      setIsVisible(false);
      setCopied(false);
    }, COPY_CLIPBOARD_TOGGLE_TIMEOUT);

    navigator.clipboard.writeText(url);
  };

  return (
    <div className={cx(styles.share, className)}>
      {isVisible && <button onClick={toggleTooltip} className={styles.backdrop} />}
      <button
        onClick={toggleTooltip}
        className={cx(styles.shareButton, {
          [styles.active]: isVisible,
        })}
      >
        <span className="buttonTypography">Share this {label}</span>
        <Icon className={styles.shareIcon} type={IconType.Share} />
      </button>

      <div
        className={cx(styles.tooltip, {
          [styles.tooltipVisible]: isVisible,
          [styles.tooltipLinkCopied]: copied,
        })}
      >
        {copied ? (
          <span role="alert" className="buttonTypography">
            Link copied
          </span>
        ) : (
          <>
            <FacebookShareButton
              url={url}
              onClick={() =>
                trackEvent(Event.SOCIAL_MEDIA_SHARE, {
                  socialType: 'Facebook',
                })
              }
            >
              <Icon className={styles.icon} type={IconType.FacebookSquare} />
            </FacebookShareButton>
            <LinkedinShareButton
              url={url}
              onClick={() =>
                trackEvent(Event.SOCIAL_MEDIA_SHARE, {
                  socialType: 'LinkedIn',
                })
              }
            >
              <Icon className={styles.icon} type={IconType.Linkedin} />
            </LinkedinShareButton>

            <button
              aria-label="Copy to clipboard"
              className={styles.clipboard}
              onClick={copyToClipboard}
            >
              <Icon className={styles.icon} type={IconType.Link} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
